/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { DatePicker, Button, Upload, Icon, Input, message } from 'antd'
import moment from 'moment'

import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import { strCol, numCol, intCol } from '@integec/grid-tools/lib/cols'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
} from 'core/utils'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'

import { DatePickerContext, SpinContext, ErrorContext } from 'context'
import {
  getEnvConfig,
  getPermitActions,
  getApiTargetInfo,
  ExportComponent,
} from 'core'
import * as ScreenPermissions from 'core/EventActions'
import { CriteriaRow, Title, FullHeightSizer, Content } from 'core/screen-util'
import {
  getForwardInterfaceImport,
  getAuthorization,
  getCredentials,
} from 'api/pgads.js'
import JcTmpErrorDialog from 'core/component/JcTmpErrorDialog'

const ForwardImportHeaders = [
  intCol({ ident: 'year', display: 'Year', isKey: true }),
  intCol({ ident: 'month', display: 'Month', isKey: true }),
  strCol({
    ident: 'textbox37',
    display: 'Textbox37',
    isFiltered: true,
    width: 300,
  }),
  strCol({ ident: 'system', display: 'System', isKey: true }),
  numCol({
    ident: 'averageWindspeed',
    display: 'AverageWindspeed',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'producedenergy',
    display: 'Producedenergy',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'expectedenergy',
    display: 'Expectedenergy',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'productionEfficiencyRatio',
    display: 'ProductionEfficiencyRatio',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'consumptionenergy',
    display: 'Consumptionenergy',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'capacityfactor',
    display: 'Capacityfactor',
    isFiltered: true,
    width: 150,
  }),
  numCol({ ident: 'availability', display: 'Availability', isFiltered: true }),
  strCol({
    ident: 'gridoperatingtime',
    display: 'Gridoperatingtime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'turbineoktime',
    display: 'Turbineoktime',
    isFiltered: true,
    width: 150,
  }),
  strCol({ ident: 'downtime', display: 'Downtime', isFiltered: true }),
  strCol({
    ident: 'maintenancetime',
    display: 'Maintenancetime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'repairtime',
    display: 'Repairtime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'gridoutagetime',
    display: 'Gridoutagetime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'weatheroutagetime',
    display: 'Weatheroutagetime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'externalstoptime',
    display: 'Externalstoptime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'externalstoppowertime',
    display: 'Externalstoppowertime',
    isFiltered: true,
    width: 200,
  }),
  strCol({
    ident: 'customerstoptime',
    display: 'Customerstoptime',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'textbox38',
    display: 'Textbox38',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'countersum',
    display: 'Countersum',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'scadadatacoverage',
    display: 'Scadadatacoverage',
    isFiltered: true,
    width: 150,
  }),
  strCol({
    ident: 'textbox39',
    display: 'Textbox39',
    isFiltered: true,
    width: 150,
  }),
  modifierCol,
  modifiedDateCol,
]

const ForwardImportScreen = ({ apiTargetInfo, envConfig, actionPermits }) => {
  const [mwImportData, setMwImportData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const [showMessages, setShowMessages] = useState(false)
  const [messages, setMessages] = useState([])
  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)

  const fetchMwImportData = () => {
    setSpinning(true)
    getForwardInterfaceImport(apiTargetInfo)({
      date: moment(selectedDate).format('MM/DD/YYYY'),
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
    }).fork(setError, data => {
      setSpinning(false)
      setMwImportData(data)
    })
  }

  const fileProps = {
    name: 'file',
    action: `${apiTargetInfo.baseUrl}/interface-data/import-parser`,
    method: 'POST',
    data: {
      importType: 'FORWARD',
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
    },
    headers: getAuthorization(apiTargetInfo),
    ...getCredentials(apiTargetInfo),
  }

  const { authGodMode } = envConfig
  const GridData = useRef()
  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <CriteriaRow>
        <Title
          css={{
            marginRight: '15px',
            marginBottom: '5px',
          }}
        >
          Forward Import
        </Title>

        <DatePicker.MonthPicker
          allowClear={false}
          format="MMMM-YYYY"
          value={selectedDate}
          onChange={d => {
            setSelectedDate(d.startOf('month'))
          }}
        />

        <Button
          loading={spinning}
          onClick={fetchMwImportData}
          css={{ marginLeft: '10px' }}
          type="primary"
          icon="database"
        >
          Retrieve
        </Button>
        <div style={{ marginLeft: '50px' }} />
        <Upload
          {...fileProps}
          onChange={data => {
            if (
              data.file.response &&
              data.file.response.restfulStatus &&
              data.file.response.restfulStatus === 'FAILURE'
            ) {
              // setShowMessages(true)
              // setMessages([data.file.response])
              setError(data.file.response)
            } else if (data.file.response && data.file.response.length === 0) {
              //  if (data.file.response) {
              message.success('File imported successfully')
              // }
            } else if (data.file.response && data.file.response.length > 0) {
              setShowMessages(true)
              setMessages(data.file.response)
              //  if (data.file.response) {
              // message.success('File imported successfully')
              // }
            }
          }}
          multiple={false}
          showUploadList={false}
        >
          <Button
            disabled={
              !hasAccesPermits({
                authGodMode,
                actionPermits,
                eventActions: [ScreenPermissions.FORWARD_IMPORT_ADMIN],
                matchAny: true,
              })
            }
          >
            <Icon type="upload" /> Import
          </Button>
        </Upload>

        <div css={{ flex: '1 1 0px' }} />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{ marginRight: '20px', flex: '0 0 200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={
            exportDataComponent
              ? _ => exportDataComponent(GridData, mwImportData, fuzzyFilter)
              : undefined
          }
          data={mwImportData}
          headers={ForwardImportHeaders}
          fileName={`Forward Import-${selectedDate.format(
            'YYYY-MM-DD HH-mm-ss'
          )}`}
          btnStyle={{ marginRight: '10px' }}
          disabled={mwImportData.length <= 0}
        />
      </CriteriaRow>
      <FullHeightSizer heightOffset={230}>
        {({ height, width }) => (
          <Content style={{ height: height - 20 }}>
            <Grid
              ref={GridData}
              data={mwImportData}
              headers={ForwardImportHeaders}
              altBgColor="#ebebeb"
              render={virtualizedGridRenderer({
                autoFixColByKey: false,
                rowHeaderRender,
                height: height - 50,
                width: width - 150,
                headerRowHeight: 30,
              })}
              fuzzyFilter={fuzzyFilter}
              initialSortOptions={[
                strCol({
                  ident: 'system',
                  display: 'System',
                  sortOrder: 'asc',
                }),
              ]}
            />
          </Content>
        )}
      </FullHeightSizer>
      {showMessages && (
        <JcTmpErrorDialog
          errors={messages}
          visible={showMessages}
          closeErrorPrompt={_ => setShowMessages(false)}
          title="Error/Warning"
        />
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
})

export default connect(mapStateToProps)(ForwardImportScreen)
